import { useState, type FormEvent } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import {
  useCreateMaintenance,
  useDeleteMaintenance,
  useMaintenance,
  useUpdateMaintenance,
} from "@/hooks/useMaintenance";
import { useSites } from "@/hooks/useSites";
import { useUrls } from "@/hooks/useUrls";
import type { MaintenanceWindow } from "@/lib/api";

function toLocalDatetimeInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("uk-UA");
  } catch {
    return iso;
  }
}

export default function MaintenancePage(): JSX.Element {
  const { toast } = useToast();
  const list = useMaintenance();
  const sitesQ = useSites();
  const createMut = useCreateMaintenance();
  const updateMut = useUpdateMaintenance();
  const deleteMut = useDeleteMaintenance();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MaintenanceWindow | null>(null);
  const [siteId, setSiteId] = useState<string>("");
  const [urlEntryId, setUrlEntryId] = useState<string>("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // Load the full list (up to the server cap) so the picker isn't truncated to
  // the default page of 50 when a site has many URLs.
  const urlsQ = useUrls(siteId ? Number(siteId) : null, { page_size: 500 });

  function reset(): void {
    setEditing(null);
    setSiteId("");
    setUrlEntryId("");
    setStartsAt("");
    setEndsAt("");
    setReason("");
    setFormError(null);
  }

  function openCreate(): void {
    reset();
    setOpen(true);
  }

  function openEdit(m: MaintenanceWindow): void {
    setEditing(m);
    setSiteId(m.site_id ? String(m.site_id) : "");
    setUrlEntryId(m.url_entry_id ? String(m.url_entry_id) : "");
    setStartsAt(toLocalDatetimeInput(m.starts_at));
    setEndsAt(toLocalDatetimeInput(m.ends_at));
    setReason(m.reason ?? "");
    setFormError(null);
    setOpen(true);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);
    if (!startsAt || !endsAt) {
      setFormError("Вкажіть дати початку та завершення.");
      return;
    }
    const startsIso = new Date(startsAt).toISOString();
    const endsIso = new Date(endsAt).toISOString();
    if (endsIso <= startsIso) {
      setFormError("Кінець має бути пізніше за початок.");
      return;
    }
    const body = {
      site_id: siteId ? Number(siteId) : null,
      url_entry_id: urlEntryId ? Number(urlEntryId) : null,
      starts_at: startsIso,
      ends_at: endsIso,
      reason: reason.trim() || null,
    };
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, body });
        toast({ title: "Оновлено", variant: "success" });
      } else {
        await createMut.mutateAsync(body);
        toast({ title: "Створено", variant: "success" });
      }
      setOpen(false);
      reset();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Не вдалося зберегти.";
      setFormError(detail);
    }
  }

  async function onDelete(id: number): Promise<void> {
    if (!window.confirm("Видалити вікно обслуговування?")) return;
    try {
      await deleteMut.mutateAsync(id);
      toast({ title: "Видалено", variant: "success" });
    } catch {
      toast({ title: "Не вдалося видалити", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Вікна обслуговування</h1>
          <p className="text-sm text-muted-foreground">
            Інтервали, коли сповіщення тимчасово вимкнено.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Додати вікно
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Початок</TableHead>
              <TableHead>Завершення</TableHead>
              <TableHead>Область</TableHead>
              <TableHead>Причина</TableHead>
              <TableHead className="text-right">Дії</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Завантаження…
                </TableCell>
              </TableRow>
            ) : !list.data || list.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Запланованих вікон немає.
                </TableCell>
              </TableRow>
            ) : (
              list.data.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{formatDate(m.starts_at)}</TableCell>
                  <TableCell>{formatDate(m.ends_at)}</TableCell>
                  <TableCell className="text-xs">
                    {m.url_entry_id
                      ? `URL #${m.url_entry_id}`
                      : m.site_id
                        ? `Site #${m.site_id}`
                        : "Глобально"}
                  </TableCell>
                  <TableCell className="max-w-md truncate">
                    {m.reason ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(m)}
                        aria-label="Редагувати"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(m.id)}
                        aria-label="Видалити"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? "Редагувати вікно" : "Нове вікно обслуговування"}
            </DialogTitle>
            <DialogDescription>
              Залиште сайт і URL порожніми для глобального вікна.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Сайт</Label>
                <Select
                  value={siteId || "none"}
                  onValueChange={(v) => {
                    setSiteId(v === "none" ? "" : v);
                    setUrlEntryId("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Глобально" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Глобально</SelectItem>
                    {sitesQ.data?.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>URL</Label>
                <Select
                  value={urlEntryId || "none"}
                  onValueChange={(v) => setUrlEntryId(v === "none" ? "" : v)}
                  disabled={!siteId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Усі URL сайту" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Усі URL сайту</SelectItem>
                    {urlsQ.data?.map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.method} {u.url}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="m-start">Початок</Label>
                <Input
                  id="m-start"
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-end">Завершення</Label>
                <Input
                  id="m-end"
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-reason">Причина</Label>
              <Textarea
                id="m-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            {formError ? (
              <p className="text-sm text-destructive">{formError}</p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Скасувати
              </Button>
              <Button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
              >
                {createMut.isPending || updateMut.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Збереження…
                  </>
                ) : editing ? (
                  "Зберегти"
                ) : (
                  "Створити"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
